// ========================
// Main Menu UI - Clean Version
// ========================

import { Renderer } from '@client/core/Renderer';
import { ClassName } from '@shared/types';
import { getSaveManager } from '@client/save/SaveManager';
import { getAudioManager } from '@client/audio/AudioManager';
import { t, setLocale, getCurrentLocale } from '@locales/index';

type SettingsTab = 'audio' | 'language';

const SETTINGS_STORAGE_KEY = 'arpg_settings';

interface SavedSettings {
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  locale: string;
}

function loadSavedSettings(): SavedSettings | null {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (raw) return JSON.parse(raw) as SavedSettings;
  } catch { /* ignore */ }
  return null;
}

function saveSettings(settings: SavedSettings): void {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch { /* ignore */ }
}

export type MenuScreen = 'main' | 'newgame' | 'loadgame' | 'settings' | 'credits';

interface Button {
  id: string;
  label: string;
  enabled: boolean;
}

interface ClassInfo {
  id: ClassName;
  name: string;
  title: string;
  desc: string;
  color: string;
  secondaryColor: string;
  locked?: boolean;
}

// Particle for atmospheric background
interface MenuParticle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  alpha: number;
  life: number;
  maxLife: number;
  color: string;
}

export class MainMenuUI {
  private screen: MenuScreen = 'main';
  private width: number;
  private height: number;
  private time: number = 0;
  private selectedClass: ClassName | null = null;
  private hoveredButton: string | null = null;
  private hoveredClass: ClassName | null = null;
  private mouseX: number = 0;
  private mouseY: number = 0;

  // Smooth hover transition values (0-1 lerp)
  private hoverValues: Map<string, number> = new Map();

  // Screen transition animation
  private screenTransitionAlpha: number = 0; // 0 = fully visible current, 1 = fully faded
  private screenTransitionPhase: 'none' | 'fade_out' | 'fade_in' = 'none';
  private pendingScreen: MenuScreen | null = null;
  private screenTransitionSpeed: number = 4; // transitions per second

  // Atmospheric particle system
  private particles: MenuParticle[] = [];
  private maxParticles: number = 60;

  // Title glow pulse
  private titleGlowPhase: number = 0;

  // Settings state
  private settingsTab: SettingsTab = 'audio';
  private draggingSlider: string | null = null;

  private onNewGame: ((className: ClassName) => void) | null = null;
  private onLoadGame: ((slot: number) => void) | null = null;
  private onLoadAutoSave: (() => void) | null = null;

  private mainButtons: Button[] = [
    { id: 'newgame', label: 'ui.newGame', enabled: true },
    { id: 'loadgame', label: 'ui.continue', enabled: true },
    { id: 'settings', label: 'ui.settings', enabled: true },
    { id: 'credits', label: 'mainMenu.infoDev', enabled: true }
  ];

  private classes: ClassInfo[] = [
    {
      id: 'alp',
      name: 'ALP',
      title: 'mainMenu.warrior',
      desc: 'mainMenu.warriorDesc',
      color: '#b87333',
      secondaryColor: '#8b4513'
    },
    {
      id: 'kam',
      name: 'KAM',
      title: 'mainMenu.shaman',
      desc: 'mainMenu.shamanDesc',
      color: '#8a2be2',
      secondaryColor: '#4b0082',
      locked: true
    },
    {
      id: 'mergen',
      name: 'MERGEN',
      title: 'mainMenu.archer',
      desc: 'mainMenu.archerDesc',
      color: '#228b22',
      secondaryColor: '#006400',
      locked: true
    }
  ];

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.initParticles();
    this.loadSettingsFromStorage();
  }

  /** Load saved settings from localStorage and apply them */
  loadSettingsFromStorage(): void {
    const saved = loadSavedSettings();
    if (saved) {
      const audio = getAudioManager();
      audio.setMasterVolume(saved.masterVolume);
      audio.setMusicVolume(saved.musicVolume);
      audio.setSfxVolume(saved.sfxVolume);
      if (saved.locale === 'tr' || saved.locale === 'en') {
        setLocale(saved.locale);
      }
    }
  }

  /** Persist current settings to localStorage */
  private persistSettings(): void {
    const audio = getAudioManager();
    saveSettings({
      masterVolume: audio.getMasterVolume(),
      musicVolume: audio.getMusicVolume(),
      sfxVolume: audio.getSfxVolume(),
      locale: getCurrentLocale(),
    });
  }

  private initParticles(): void {
    this.particles = [];
    for (let i = 0; i < this.maxParticles; i++) {
      this.particles.push(this.createParticle(true));
    }
  }

  private createParticle(randomizeLife: boolean = false): MenuParticle {
    const isGold = Math.random() < 0.3;
    const maxLife = 8 + Math.random() * 12;
    return {
      x: Math.random() * this.width,
      y: Math.random() * this.height,
      vx: (Math.random() - 0.5) * 8,
      vy: -5 - Math.random() * 12, // slow upward drift
      size: 0.5 + Math.random() * 2,
      alpha: 0.1 + Math.random() * 0.4,
      life: randomizeLife ? Math.random() * maxLife : 0,
      maxLife,
      color: isGold ? '#ffd700' : '#aabbdd',
    };
  }

  setScreenSize(width: number, height: number): void {
    this.width = width;
    this.height = height;
  }

  setCallbacks(callbacks: {
    onNewGame?: (className: ClassName) => void;
    onLoadGame?: (slot: number) => void;
    onLoadAutoSave?: () => void;
  }): void {
    if (callbacks.onNewGame) this.onNewGame = callbacks.onNewGame;
    if (callbacks.onLoadGame) this.onLoadGame = callbacks.onLoadGame;
    if (callbacks.onLoadAutoSave) this.onLoadAutoSave = callbacks.onLoadAutoSave;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    // Title glow pulse
    this.titleGlowPhase += deltaTime * 1.5;

    // Update screen transition
    if (this.screenTransitionPhase === 'fade_out') {
      this.screenTransitionAlpha += deltaTime * this.screenTransitionSpeed;
      if (this.screenTransitionAlpha >= 1) {
        this.screenTransitionAlpha = 1;
        // Apply the actual screen change at peak fade
        if (this.pendingScreen !== null) {
          this.screen = this.pendingScreen;
          this.pendingScreen = null;
        }
        this.screenTransitionPhase = 'fade_in';
      }
    } else if (this.screenTransitionPhase === 'fade_in') {
      this.screenTransitionAlpha -= deltaTime * this.screenTransitionSpeed;
      if (this.screenTransitionAlpha <= 0) {
        this.screenTransitionAlpha = 0;
        this.screenTransitionPhase = 'none';
      }
    }

    // Update particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life += deltaTime;
      p.x += p.vx * deltaTime;
      p.y += p.vy * deltaTime;

      // Fade in/out based on life
      const lifeRatio = p.life / p.maxLife;
      if (lifeRatio < 0.1) {
        p.alpha = (lifeRatio / 0.1) * 0.4;
      } else if (lifeRatio > 0.8) {
        p.alpha = ((1 - lifeRatio) / 0.2) * 0.4;
      }

      // Respawn dead particles
      if (p.life >= p.maxLife || p.x < -20 || p.x > this.width + 20 || p.y < -20) {
        this.particles[i] = this.createParticle(false);
        // Spawn from bottom or sides
        const spawn = Math.random();
        if (spawn < 0.6) {
          this.particles[i].y = this.height + 10;
          this.particles[i].x = Math.random() * this.width;
        } else if (spawn < 0.8) {
          this.particles[i].x = -10;
          this.particles[i].y = Math.random() * this.height;
          this.particles[i].vx = Math.abs(this.particles[i].vx);
        } else {
          this.particles[i].x = this.width + 10;
          this.particles[i].y = Math.random() * this.height;
          this.particles[i].vx = -Math.abs(this.particles[i].vx);
        }
      }
    }

    // Smooth hover transitions
    const lerpSpeed = 8 * deltaTime;
    // Update all tracked hover values
    for (const [key, value] of this.hoverValues) {
      const isHovered = this.hoveredButton === key || this.hoveredClass === key;
      const target = isHovered ? 1 : 0;
      const newVal = value + (target - value) * Math.min(1, lerpSpeed);
      if (Math.abs(newVal - target) < 0.01) {
        if (target === 0) {
          this.hoverValues.delete(key);
        } else {
          this.hoverValues.set(key, 1);
        }
      } else {
        this.hoverValues.set(key, newVal);
      }
    }
    // Ensure hovered items have entries
    if (this.hoveredButton && !this.hoverValues.has(this.hoveredButton)) {
      this.hoverValues.set(this.hoveredButton, 0);
    }
    if (this.hoveredClass && !this.hoverValues.has(this.hoveredClass)) {
      this.hoverValues.set(this.hoveredClass, 0);
    }
  }

  private getHoverValue(key: string): number {
    return this.hoverValues.get(key) || 0;
  }

  handleMouseMove(x: number, y: number): void {
    this.mouseX = x;
    this.mouseY = y;
    this.updateHoverStates();
  }

  handleClick(x: number, y: number): boolean {
    this.mouseX = x;
    this.mouseY = y;
    const audio = getAudioManager();

    if (this.screen === 'main') {
      const btnIndex = this.getButtonAtPosition(x, y);
      if (btnIndex >= 0) {
        const btn = this.mainButtons[btnIndex];
        if (btn.enabled) {
          audio.play('ui_click');
          switch (btn.id) {
            case 'newgame': {
              // Classless system: skip class selection, start game directly
              if (this.onNewGame) {
                audio.play('ui_confirm');
                this.onNewGame('alp' as any); // Legacy compat - class doesn't matter
                return true;
              }
              this.transitionToScreen('newgame');
              break;
            }
            case 'loadgame': this.transitionToScreen('loadgame'); break;
            case 'settings': this.transitionToScreen('settings'); break;
            case 'credits': this.transitionToScreen('credits'); break;
          }
          return true;
        }
      }
    } else if (this.screen === 'newgame') {
      // Check class selection
      const classIndex = this.getClassAtPosition(x, y);
      if (classIndex >= 0) {
        const cls = this.classes[classIndex];
        if (cls.locked) {
          // Locked class - play error sound, don't select
          audio.play('ui_error');
          return true;
        }
        audio.play('ui_click');
        this.selectedClass = cls.id;
        return true;
      }

      // Check start button
      if (this.selectedClass && this.isInStartButton(x, y)) {
        audio.play('ui_confirm');
        if (this.onNewGame) {
          this.onNewGame(this.selectedClass);
        }
        return true;
      }

      // Check back button
      if (this.isInBackButton(x, y)) {
        audio.play('ui_close');
        this.transitionToScreen('main');
        this.selectedClass = null;
        return true;
      }
    } else if (this.screen === 'settings') {
      // Settings screen interactions
      if (this.isInBackButton(x, y)) {
        audio.play('ui_close');
        this.transitionToScreen('main');
        return true;
      }

      // Tab buttons
      const tabResult = this.getSettingsTabAtPosition(x, y);
      if (tabResult) {
        if (tabResult !== this.settingsTab) {
          audio.play('ui_click');
          this.settingsTab = tabResult;
        }
        return true;
      }

      // Audio tab - slider clicks
      if (this.settingsTab === 'audio') {
        const sliderHit = this.getSliderAtPosition(x, y);
        if (sliderHit) {
          this.draggingSlider = sliderHit;
          this.applySliderDrag(x);
          return true;
        }
      }

      // Language tab - language buttons
      if (this.settingsTab === 'language') {
        const lang = this.getLanguageButtonAtPosition(x, y);
        if (lang) {
          audio.play('ui_click');
          setLocale(lang);
          this.persistSettings();
          return true;
        }
      }
    } else {
      // Other screens - back button
      if (this.isInBackButton(x, y)) {
        audio.play('ui_close');
        this.transitionToScreen('main');
        return true;
      }

      // Load game slots
      if (this.screen === 'loadgame') {
        const slot = this.getSlotAtPosition(x, y);
        if (slot === -1 && this.onLoadAutoSave) {
          // Auto-save slot
          audio.play('ui_confirm');
          this.onLoadAutoSave();
          return true;
        } else if (slot >= 0 && this.onLoadGame) {
          audio.play('ui_confirm');
          this.onLoadGame(slot);
          return true;
        }
      }
    }

    return false;
  }

  /** Handle mouse down (for slider drag start) */
  handleMouseDown(x: number, y: number): boolean {
    if (this.screen === 'settings' && this.settingsTab === 'audio') {
      const sliderHit = this.getSliderAtPosition(x, y);
      if (sliderHit) {
        this.draggingSlider = sliderHit;
        this.applySliderDrag(x);
        return true;
      }
    }
    return false;
  }

  /** Handle mouse up (for slider drag end) */
  handleMouseUp(): void {
    if (this.draggingSlider) {
      this.draggingSlider = null;
      this.persistSettings();
    }
  }

  /** Handle mouse drag (for sliders) */
  handleMouseDrag(x: number, _y: number): void {
    if (this.draggingSlider) {
      this.applySliderDrag(x);
    }
  }

  private updateHoverStates(): void {
    this.hoveredButton = null;
    this.hoveredClass = null;

    if (this.screen === 'main') {
      const btnIndex = this.getButtonAtPosition(this.mouseX, this.mouseY);
      if (btnIndex >= 0) {
        this.hoveredButton = this.mainButtons[btnIndex].id;
      }
    } else if (this.screen === 'newgame') {
      const classIndex = this.getClassAtPosition(this.mouseX, this.mouseY);
      if (classIndex >= 0) {
        this.hoveredClass = this.classes[classIndex].id;
      }
      if (this.isInStartButton(this.mouseX, this.mouseY)) {
        this.hoveredButton = 'start';
      }
      if (this.isInBackButton(this.mouseX, this.mouseY)) {
        this.hoveredButton = 'back';
      }
    } else {
      if (this.isInBackButton(this.mouseX, this.mouseY)) {
        this.hoveredButton = 'back';
      }
    }
  }

  private getButtonAtPosition(x: number, y: number): number {
    const btnW = 260;
    const btnH = 55;
    const startY = this.height / 2;
    const centerX = this.width / 2;

    for (let i = 0; i < this.mainButtons.length; i++) {
      const btnY = startY + i * 70;
      if (x >= centerX - btnW / 2 && x <= centerX + btnW / 2 &&
          y >= btnY && y <= btnY + btnH) {
        return i;
      }
    }
    return -1;
  }

  private getClassAtPosition(x: number, y: number): number {
    const cardW = 200;
    const cardH = 250;
    const gap = 30;
    const totalW = this.classes.length * cardW + (this.classes.length - 1) * gap;
    const startX = (this.width - totalW) / 2;
    const cardY = this.height / 2 - 60;

    for (let i = 0; i < this.classes.length; i++) {
      const cardX = startX + i * (cardW + gap);
      if (x >= cardX && x <= cardX + cardW && y >= cardY && y <= cardY + cardH) {
        return i;
      }
    }
    return -1;
  }

  private isInStartButton(x: number, y: number): boolean {
    const btnW = 200;
    const btnH = 50;
    const btnX = this.width / 2 - btnW / 2;
    const btnY = this.height - 100;
    return x >= btnX && x <= btnX + btnW && y >= btnY && y <= btnY + btnH;
  }

  private isInBackButton(x: number, y: number): boolean {
    return x >= 30 && x <= 130 && y >= this.height - 60 && y <= this.height - 20;
  }

  private getSlotAtPosition(x: number, y: number): number {
    const slotW = 400;
    const slotH = 80;
    const autoSaveY = 130;
    const startY = 230;
    const centerX = this.width / 2;

    // Check auto-save slot first
    const saveManager = getSaveManager();
    if (saveManager.hasAutoSave()) {
      if (x >= centerX - slotW / 2 && x <= centerX + slotW / 2 &&
          y >= autoSaveY && y <= autoSaveY + slotH) {
        return -1; // -1 indicates auto-save
      }
    }

    // Check regular save slots
    for (let i = 0; i < 3; i++) {
      const slotY = startY + i * 100;
      if (x >= centerX - slotW / 2 && x <= centerX + slotW / 2 &&
          y >= slotY && y <= slotY + slotH) {
        return i;
      }
    }
    return -2; // Nothing selected
  }

  private transitionToScreen(target: MenuScreen): void {
    if (this.screenTransitionPhase !== 'none') return; // already transitioning
    this.pendingScreen = target;
    this.screenTransitionPhase = 'fade_out';
    this.screenTransitionAlpha = 0;
  }

  render(renderer: Renderer): void {
    const ctx = renderer.context;

    this.drawBackground(ctx);
    this.drawParticles(ctx);

    switch (this.screen) {
      case 'main':
        this.drawMainMenu(ctx);
        break;
      case 'newgame':
        this.drawNewGame(ctx);
        break;
      case 'loadgame':
        this.drawLoadGame(ctx);
        break;
      case 'settings':
        this.drawSettings(ctx);
        break;
      case 'credits':
        this.drawCredits(ctx);
        break;
    }

    // Screen transition fade overlay
    if (this.screenTransitionAlpha > 0) {
      ctx.save();
      ctx.globalAlpha = this.screenTransitionAlpha;
      ctx.fillStyle = '#0a0a14';
      ctx.fillRect(0, 0, this.width, this.height);
      ctx.restore();
    }
  }

  private drawBackground(ctx: CanvasRenderingContext2D): void {
    // Gradient background
    const grad = ctx.createLinearGradient(0, 0, 0, this.height);
    grad.addColorStop(0, '#0c0c18');
    grad.addColorStop(0.5, '#141428');
    grad.addColorStop(1, '#0a0a14');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, this.width, this.height);

    // Stars
    ctx.fillStyle = '#fff';
    for (let i = 0; i < 80; i++) {
      const px = (i * 173) % this.width;
      const py = (i * 97) % this.height;
      const twinkle = Math.sin(this.time * 2 + i) * 0.5 + 0.5;
      ctx.globalAlpha = twinkle * 0.6;
      ctx.beginPath();
      ctx.arc(px, py, (i % 3) * 0.4 + 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // Subtle glow in center
    const centerGlow = ctx.createRadialGradient(
      this.width / 2, this.height / 2, 0,
      this.width / 2, this.height / 2, 400
    );
    centerGlow.addColorStop(0, 'rgba(100, 80, 150, 0.1)');
    centerGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = centerGlow;
    ctx.fillRect(0, 0, this.width, this.height);
  }

  private drawParticles(ctx: CanvasRenderingContext2D): void {
    ctx.save();
    for (const p of this.particles) {
      ctx.globalAlpha = p.alpha;
      ctx.fillStyle = p.color;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();

      // Small glow around golden particles
      if (p.color === '#ffd700' && p.size > 1) {
        ctx.globalAlpha = p.alpha * 0.3;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
        const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 3);
        glow.addColorStop(0, 'rgba(255, 215, 0, 0.3)');
        glow.addColorStop(1, 'rgba(255, 215, 0, 0)');
        ctx.fillStyle = glow;
        ctx.fill();
      }
    }
    ctx.globalAlpha = 1;
    ctx.restore();
  }

  private drawMainMenu(ctx: CanvasRenderingContext2D): void {
    // Title with pulsing glow effect
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const titleX = this.width / 2;
    const titleY = this.height / 2 - 180;

    // Outer pulsing glow layer
    const pulseIntensity = Math.sin(this.titleGlowPhase) * 0.5 + 0.5;
    const outerGlowBlur = 40 + pulseIntensity * 25;
    ctx.save();
    ctx.shadowColor = `rgba(255, 180, 0, ${0.6 + pulseIntensity * 0.4})`;
    ctx.shadowBlur = outerGlowBlur;
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 64px serif';
    ctx.fillText(t('mainMenu.gameTitle'), titleX, titleY);
    ctx.restore();

    // Second pass: sharper inner glow
    ctx.save();
    ctx.shadowColor = '#fff8e0';
    ctx.shadowBlur = 8 + pulseIntensity * 5;
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 64px serif';
    ctx.fillText(t('mainMenu.gameTitle'), titleX, titleY);
    ctx.restore();

    // Title text itself (crisp, on top)
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 64px serif';
    ctx.fillText(t('mainMenu.gameTitle'), titleX, titleY);

    // Subtle underline bar that pulses
    const barWidth = 200 + pulseIntensity * 30;
    const barAlpha = 0.3 + pulseIntensity * 0.2;
    const barGrad = ctx.createLinearGradient(titleX - barWidth / 2, 0, titleX + barWidth / 2, 0);
    barGrad.addColorStop(0, 'rgba(255, 215, 0, 0)');
    barGrad.addColorStop(0.3, `rgba(255, 215, 0, ${barAlpha})`);
    barGrad.addColorStop(0.5, `rgba(255, 230, 100, ${barAlpha + 0.1})`);
    barGrad.addColorStop(0.7, `rgba(255, 215, 0, ${barAlpha})`);
    barGrad.addColorStop(1, 'rgba(255, 215, 0, 0)');
    ctx.fillStyle = barGrad;
    ctx.fillRect(titleX - barWidth / 2, titleY + 35, barWidth, 2);

    ctx.fillStyle = '#888';
    ctx.font = '20px sans-serif';
    ctx.fillText(t('mainMenu.gameSubtitle'), this.width / 2, this.height / 2 - 130);

    // Buttons
    const btnW = 260;
    const btnH = 55;
    const startY = this.height / 2;

    for (let i = 0; i < this.mainButtons.length; i++) {
      const btn = this.mainButtons[i];
      const btnY = startY + i * 70;
      const isHovered = this.hoveredButton === btn.id;
      const hoverT = this.getHoverValue(btn.id);

      this.drawButton(ctx, this.width / 2 - btnW / 2, btnY, btnW, btnH, t(btn.label).toUpperCase(), isHovered, btn.enabled, hoverT);
    }

    // Version
    ctx.fillStyle = '#444';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'right';
    ctx.fillText('v0.1.0', this.width - 20, this.height - 15);
  }

  private drawButton(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number,
                     label: string, hovered: boolean, enabled: boolean, hoverT?: number): void {
    // Use smooth hover transition value if available
    const t2 = hoverT !== undefined ? hoverT : (hovered ? 1 : 0);

    // Background with interpolated colors
    if (!enabled) {
      ctx.fillStyle = '#1a1a1a';
    } else {
      const grad = ctx.createLinearGradient(x, y, x, y + h);
      const r1 = Math.round(0x1a + (0x3a - 0x1a) * t2);
      const g1 = Math.round(0x1a + (0x30 - 0x1a) * t2);
      const b1 = Math.round(0x20 + (0x20 - 0x20) * t2);
      const r2 = Math.round(0x25 + (0x4a - 0x25) * t2);
      const g2 = Math.round(0x25 + (0x40 - 0x25) * t2);
      const b2 = Math.round(0x30 + (0x30 - 0x30) * t2);
      grad.addColorStop(0, `rgb(${r1},${g1},${b1})`);
      grad.addColorStop(0.5, `rgb(${r2},${g2},${b2})`);
      grad.addColorStop(1, `rgb(${r1},${g1},${b1})`);
      ctx.fillStyle = grad;
    }

    // Scale effect on hover
    if (t2 > 0.01 && enabled) {
      const scale = 1 + t2 * 0.02;
      const cx = x + w / 2;
      const cy = y + h / 2;
      ctx.save();
      ctx.translate(cx, cy);
      ctx.scale(scale, scale);
      ctx.translate(-cx, -cy);
    }

    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 6);
    ctx.fill();

    // Border with interpolated color
    const borderR = Math.round(0x3a + (0xff - 0x3a) * t2);
    const borderG = Math.round(0x3a + (0xd7 - 0x3a) * t2);
    const borderB = Math.round(0x4a + (0x00 - 0x4a) * t2);
    ctx.strokeStyle = enabled ? `rgb(${borderR},${borderG},${borderB})` : '#3a3a4a';
    ctx.lineWidth = 1 + t2;
    ctx.stroke();

    // Text with interpolated color
    const textR = Math.round(0xcc + (0xff - 0xcc) * t2);
    const textG = Math.round(0xcc + (0xd7 - 0xcc) * t2);
    const textB = Math.round(0xcc + (0x00 - 0xcc) * t2);
    ctx.fillStyle = !enabled ? '#444' : `rgb(${textR},${textG},${textB})`;
    ctx.font = 'bold 18px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, x + w / 2, y + h / 2);

    // Restore scale transform
    if (t2 > 0.01 && enabled) {
      ctx.restore();
    }
  }

  private drawNewGame(ctx: CanvasRenderingContext2D): void {
    // Title
    ctx.textAlign = 'center';
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 36px serif';
    ctx.fillText(t('mainMenu.selectClass'), this.width / 2, 70);

    // Class cards
    const cardW = 200;
    const cardH = 250;
    const gap = 30;
    const totalW = this.classes.length * cardW + (this.classes.length - 1) * gap;
    const startX = (this.width - totalW) / 2;
    const cardY = this.height / 2 - 60;

    for (let i = 0; i < this.classes.length; i++) {
      const cls = this.classes[i];
      const cardX = startX + i * (cardW + gap);
      const isSelected = this.selectedClass === cls.id;
      const isHovered = this.hoveredClass === cls.id;

      this.drawClassCard(ctx, cardX, cardY, cardW, cardH, cls, isSelected, isHovered);
    }

    // Start button
    if (this.selectedClass) {
      const btnW = 200;
      const btnH = 50;
      const btnX = this.width / 2 - btnW / 2;
      const btnY = this.height - 100;
      const isHovered = this.hoveredButton === 'start';

      ctx.fillStyle = isHovered ? '#2a6a2a' : '#1a4a1a';
      ctx.beginPath();
      ctx.roundRect(btnX, btnY, btnW, btnH, 6);
      ctx.fill();

      ctx.strokeStyle = isHovered ? '#5a5' : '#3a3';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = '#fff';
      ctx.font = 'bold 18px sans-serif';
      ctx.fillText(t('mainMenu.start'), this.width / 2, btnY + btnH / 2);
    }

    // Back button
    this.drawBackButton(ctx);
  }

  private drawClassCard(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number,
                        cls: ClassInfo, selected: boolean, hovered: boolean): void {
    // Card background
    const bgGrad = ctx.createLinearGradient(x, y, x, y + h);
    if (selected) {
      bgGrad.addColorStop(0, '#2a2820');
      bgGrad.addColorStop(1, '#1a1810');
    } else {
      bgGrad.addColorStop(0, '#18181f');
      bgGrad.addColorStop(1, '#10101a');
    }
    ctx.fillStyle = bgGrad;
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 10);
    ctx.fill();

    // Border
    ctx.strokeStyle = selected ? '#ffd700' : hovered ? cls.color : '#2a2a3a';
    ctx.lineWidth = selected ? 3 : 2;
    ctx.stroke();

    // Class icon circle
    const iconY = y + 60;
    const iconGrad = ctx.createRadialGradient(x + w / 2, iconY, 0, x + w / 2, iconY, 40);
    iconGrad.addColorStop(0, cls.color + '40');
    iconGrad.addColorStop(1, cls.color + '10');
    ctx.fillStyle = iconGrad;
    ctx.beginPath();
    ctx.arc(x + w / 2, iconY, 40, 0, Math.PI * 2);
    ctx.fill();

    ctx.strokeStyle = cls.color;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Class initial letter
    ctx.fillStyle = cls.color;
    ctx.font = 'bold 36px serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(cls.name.charAt(0), x + w / 2, iconY);

    // Class name
    ctx.fillStyle = cls.color;
    ctx.font = 'bold 22px serif';
    ctx.fillText(cls.name, x + w / 2, y + 120);

    // Class title
    ctx.fillStyle = '#888';
    ctx.font = '14px sans-serif';
    ctx.fillText(t(cls.title), x + w / 2, y + 145);

    // Description
    ctx.fillStyle = '#666';
    ctx.font = '12px sans-serif';
    const words = t(cls.desc).split(' ');
    let line = '';
    let lineY = y + 175;
    const maxW = w - 20;

    for (const word of words) {
      const test = line + word + ' ';
      if (ctx.measureText(test).width > maxW && line) {
        ctx.fillText(line.trim(), x + w / 2, lineY);
        line = word + ' ';
        lineY += 16;
      } else {
        line = test;
      }
    }
    ctx.fillText(line.trim(), x + w / 2, lineY);

    // Selection indicator
    if (selected && !cls.locked) {
      ctx.fillStyle = '#ffd700';
      ctx.beginPath();
      ctx.arc(x + w / 2, y + h - 20, 8, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 12px sans-serif';
      ctx.fillText('\u2713', x + w / 2, y + h - 19);
    }

    // Locked overlay
    if (cls.locked) {
      ctx.save();
      // Dark overlay
      ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 10);
      ctx.fill();

      // Lock icon
      ctx.fillStyle = '#888888';
      ctx.font = 'bold 32px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('\uD83D\uDD12', x + w / 2, y + h / 2 - 10);

      // "Coming Soon" text
      ctx.fillStyle = '#aaaaaa';
      ctx.font = '14px sans-serif';
      ctx.fillText(t('mainMenu.comingSoon') || 'Yakinda', x + w / 2, y + h / 2 + 20);
      ctx.restore();
    }
  }

  private drawLoadGame(ctx: CanvasRenderingContext2D): void {
    ctx.textAlign = 'center';
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 36px serif';
    ctx.fillText(t('mainMenu.loadGame'), this.width / 2, 70);

    const slotW = 400;
    const slotH = 80;
    const slotX = this.width / 2 - slotW / 2;
    const saveManager = getSaveManager();

    // Auto-save slot at top (if exists)
    const autoSaveInfo = saveManager.getAutoSaveInfo();
    if (autoSaveInfo) {
      const autoY = 130;
      const isHovered = this.isSlotHovered(-1);

      // Special styling for auto-save
      ctx.fillStyle = isHovered ? '#1a2a1a' : '#151f15';
      ctx.beginPath();
      ctx.roundRect(slotX, autoY, slotW, slotH, 8);
      ctx.fill();

      ctx.strokeStyle = isHovered ? '#4a4' : '#2a4a2a';
      ctx.lineWidth = isHovered ? 2 : 1;
      ctx.stroke();

      // Auto-save icon
      ctx.fillStyle = '#4ade80';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(`⟳ ${t('mainMenu.autoSave')}`, slotX + 20, autoY + 28);

      // Info
      ctx.fillStyle = '#6a6';
      ctx.font = '13px sans-serif';
      const className = this.getClassDisplayName(autoSaveInfo.className || 'alp');
      ctx.fillText(`${className} - Seviye ${autoSaveInfo.playerLevel}`, slotX + 20, autoY + 50);

      // Time
      if (autoSaveInfo.timestamp) {
        ctx.textAlign = 'right';
        ctx.fillStyle = '#484';
        ctx.font = '12px sans-serif';
        const date = new Date(autoSaveInfo.timestamp);
        ctx.fillText(this.formatDate(date), slotX + slotW - 20, autoY + 50);
      }
    }

    // Regular save slots
    const startY = autoSaveInfo ? 230 : 180;

    for (let i = 0; i < 3; i++) {
      const slotY = startY + i * 100;
      const isHovered = this.isSlotHovered(i);

      ctx.fillStyle = isHovered ? '#1a1a2a' : '#15151f';
      ctx.beginPath();
      ctx.roundRect(slotX, slotY, slotW, slotH, 8);
      ctx.fill();

      ctx.strokeStyle = isHovered ? '#4a4a6a' : '#2a2a3a';
      ctx.lineWidth = isHovered ? 2 : 1;
      ctx.stroke();

      ctx.fillStyle = '#666';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(t('mainMenu.saveSlot', { num: i + 1 }), slotX + 20, slotY + 35);

      ctx.fillStyle = '#444';
      ctx.font = '13px sans-serif';
      ctx.fillText(t('mainMenu.emptySlot'), slotX + 20, slotY + 55);
    }

    this.drawBackButton(ctx);
  }

  private isSlotHovered(slot: number): boolean {
    const slotW = 400;
    const slotH = 80;
    const centerX = this.width / 2;
    const slotX = centerX - slotW / 2;
    const saveManager = getSaveManager();
    const hasAutoSave = saveManager.hasAutoSave();

    if (slot === -1 && hasAutoSave) {
      const autoY = 130;
      return this.mouseX >= slotX && this.mouseX <= slotX + slotW &&
             this.mouseY >= autoY && this.mouseY <= autoY + slotH;
    } else if (slot >= 0) {
      const startY = hasAutoSave ? 230 : 180;
      const slotY = startY + slot * 100;
      return this.mouseX >= slotX && this.mouseX <= slotX + slotW &&
             this.mouseY >= slotY && this.mouseY <= slotY + slotH;
    }
    return false;
  }

  private getClassDisplayName(className: ClassName | string): string {
    switch (className) {
      case 'alp': return 'Alp';
      case 'kam': return 'Kam';
      case 'mergen': return 'Mergen';
      default: return className;
    }
  }

  private formatDate(date: Date): string {
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const hour = date.getHours().toString().padStart(2, '0');
    const min = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month} ${hour}:${min}`;
  }

  // ==========================================
  // Settings Panel Layout Constants
  // ==========================================
  private get settingsPanelX(): number { return this.width / 2 - 250; }
  private get settingsPanelY(): number { return 110; }
  private get settingsPanelW(): number { return 500; }
  private get settingsPanelH(): number { return this.height - 200; }
  private get settingsTabBarY(): number { return this.settingsPanelY; }
  private get settingsContentY(): number { return this.settingsTabBarY + 50; }
  private get sliderX(): number { return this.settingsPanelX + 40; }
  private get sliderW(): number { return this.settingsPanelW - 80; }

  private getSliderRect(index: number): { x: number; y: number; w: number; h: number } {
    const y = this.settingsContentY + 60 + index * 80;
    return { x: this.sliderX, y, w: this.sliderW, h: 20 };
  }

  private getSettingsTabAtPosition(x: number, y: number): SettingsTab | null {
    const tabW = 120;
    const tabH = 40;
    const tabY = this.settingsTabBarY;
    const tabStartX = this.settingsPanelX + 20;

    // Audio tab
    if (x >= tabStartX && x <= tabStartX + tabW && y >= tabY && y <= tabY + tabH) {
      return 'audio';
    }
    // Language tab
    if (x >= tabStartX + tabW + 10 && x <= tabStartX + tabW * 2 + 10 && y >= tabY && y <= tabY + tabH) {
      return 'language';
    }
    return null;
  }

  private getSliderAtPosition(x: number, y: number): string | null {
    const sliders = ['master', 'music', 'sfx'];
    for (let i = 0; i < sliders.length; i++) {
      const rect = this.getSliderRect(i);
      // Extend hit area vertically
      if (x >= rect.x - 5 && x <= rect.x + rect.w + 5 && y >= rect.y - 15 && y <= rect.y + rect.h + 15) {
        return sliders[i];
      }
    }
    return null;
  }

  private applySliderDrag(mouseX: number): void {
    if (!this.draggingSlider) return;
    const audio = getAudioManager();

    // Which slider index?
    let index = 0;
    if (this.draggingSlider === 'music') index = 1;
    else if (this.draggingSlider === 'sfx') index = 2;

    const rect = this.getSliderRect(index);
    const ratio = Math.max(0, Math.min(1, (mouseX - rect.x) / rect.w));

    switch (this.draggingSlider) {
      case 'master': audio.setMasterVolume(ratio); break;
      case 'music': audio.setMusicVolume(ratio); break;
      case 'sfx': audio.setSfxVolume(ratio); break;
    }
  }

  private getLanguageButtonAtPosition(x: number, y: number): 'tr' | 'en' | null {
    const btnW = 180;
    const btnH = 50;
    const btnY = this.settingsContentY + 80;
    const centerX = this.width / 2;

    // Turkish button
    const trX = centerX - btnW - 15;
    if (x >= trX && x <= trX + btnW && y >= btnY && y <= btnY + btnH) {
      return 'tr';
    }

    // English button
    const enX = centerX + 15;
    if (x >= enX && x <= enX + btnW && y >= btnY && y <= btnY + btnH) {
      return 'en';
    }

    return null;
  }

  private drawSettings(ctx: CanvasRenderingContext2D): void {
    // Title
    ctx.textAlign = 'center';
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 36px serif';
    ctx.fillText(t('ui.settings'), this.width / 2, 70);

    // Panel background
    const px = this.settingsPanelX;
    const py = this.settingsPanelY;
    const pw = this.settingsPanelW;
    const ph = this.settingsPanelH;

    ctx.fillStyle = 'rgba(10, 10, 20, 0.85)';
    ctx.beginPath();
    ctx.roundRect(px, py, pw, ph, 10);
    ctx.fill();
    ctx.strokeStyle = '#3a3a5a';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Tab bar
    this.drawSettingsTabBar(ctx);

    // Tab content
    if (this.settingsTab === 'audio') {
      this.drawAudioSettings(ctx);
    } else if (this.settingsTab === 'language') {
      this.drawLanguageSettings(ctx);
    }

    this.drawBackButton(ctx);
  }

  private drawSettingsTabBar(ctx: CanvasRenderingContext2D): void {
    const tabW = 120;
    const tabH = 40;
    const tabY = this.settingsTabBarY;
    const tabStartX = this.settingsPanelX + 20;

    const tabs: { id: SettingsTab; label: string }[] = [
      { id: 'audio', label: t('settings.audioTab') },
      { id: 'language', label: t('settings.languageTab') },
    ];

    for (let i = 0; i < tabs.length; i++) {
      const tab = tabs[i];
      const x = tabStartX + i * (tabW + 10);
      const isActive = this.settingsTab === tab.id;

      // Tab background
      ctx.fillStyle = isActive ? 'rgba(60, 50, 80, 0.9)' : 'rgba(25, 25, 40, 0.7)';
      ctx.beginPath();
      ctx.roundRect(x, tabY, tabW, tabH, [6, 6, 0, 0]);
      ctx.fill();

      // Tab border
      ctx.strokeStyle = isActive ? '#ffd700' : '#3a3a5a';
      ctx.lineWidth = isActive ? 2 : 1;
      ctx.stroke();

      // Tab text
      ctx.fillStyle = isActive ? '#ffd700' : '#888';
      ctx.font = `${isActive ? 'bold ' : ''}14px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(tab.label, x + tabW / 2, tabY + tabH / 2);
    }
  }

  private drawAudioSettings(ctx: CanvasRenderingContext2D): void {
    const audio = getAudioManager();
    const sliders: { label: string; value: number; key: string }[] = [
      { label: t('settings.masterVolume'), value: audio.getMasterVolume(), key: 'master' },
      { label: t('settings.musicVolume'), value: audio.getMusicVolume(), key: 'music' },
      { label: t('settings.sfxVolume'), value: audio.getSfxVolume(), key: 'sfx' },
    ];

    for (let i = 0; i < sliders.length; i++) {
      const slider = sliders[i];
      const rect = this.getSliderRect(i);
      const labelY = rect.y - 22;
      const isDragging = this.draggingSlider === slider.key;

      // Label
      ctx.fillStyle = '#ccc';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(slider.label, rect.x, labelY);

      // Value percentage
      ctx.textAlign = 'right';
      ctx.fillStyle = '#ffd700';
      ctx.fillText(`${Math.round(slider.value * 100)}%`, rect.x + rect.w, labelY);

      // Slider track background
      ctx.fillStyle = 'rgba(30, 30, 50, 0.8)';
      ctx.beginPath();
      ctx.roundRect(rect.x, rect.y, rect.w, rect.h, 4);
      ctx.fill();
      ctx.strokeStyle = '#3a3a5a';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Slider fill
      const fillW = slider.value * rect.w;
      if (fillW > 0) {
        const grad = ctx.createLinearGradient(rect.x, rect.y, rect.x + fillW, rect.y);
        grad.addColorStop(0, isDragging ? '#ffaa00' : '#d4a84b');
        grad.addColorStop(1, isDragging ? '#ffd700' : '#ffd700');
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.roundRect(rect.x, rect.y, fillW, rect.h, 4);
        ctx.fill();
      }

      // Slider knob
      const knobX = rect.x + fillW;
      const knobY = rect.y + rect.h / 2;
      const knobR = isDragging ? 10 : 8;

      ctx.beginPath();
      ctx.arc(knobX, knobY, knobR, 0, Math.PI * 2);
      ctx.fillStyle = isDragging ? '#fff' : '#e0d0a0';
      ctx.fill();
      ctx.strokeStyle = '#ffd700';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
  }

  private drawLanguageSettings(ctx: CanvasRenderingContext2D): void {
    const currentLang = getCurrentLocale();
    const btnW = 180;
    const btnH = 50;
    const btnY = this.settingsContentY + 80;
    const centerX = this.width / 2;

    // Current language indicator
    ctx.fillStyle = '#888';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('settings.currentLanguage'), centerX, this.settingsContentY + 40);

    const langs: { id: 'tr' | 'en'; label: string; flag: string }[] = [
      { id: 'tr', label: t('settings.turkish'), flag: 'TR' },
      { id: 'en', label: t('settings.english'), flag: 'EN' },
    ];

    for (let i = 0; i < langs.length; i++) {
      const lang = langs[i];
      const x = i === 0 ? centerX - btnW - 15 : centerX + 15;
      const isActive = currentLang === lang.id;
      const isHovered = this.mouseX >= x && this.mouseX <= x + btnW &&
                        this.mouseY >= btnY && this.mouseY <= btnY + btnH;

      // Button background
      if (isActive) {
        ctx.fillStyle = 'rgba(60, 50, 20, 0.9)';
      } else if (isHovered) {
        ctx.fillStyle = 'rgba(40, 40, 60, 0.8)';
      } else {
        ctx.fillStyle = 'rgba(25, 25, 40, 0.7)';
      }
      ctx.beginPath();
      ctx.roundRect(x, btnY, btnW, btnH, 8);
      ctx.fill();

      // Border
      ctx.strokeStyle = isActive ? '#ffd700' : isHovered ? '#888' : '#3a3a5a';
      ctx.lineWidth = isActive ? 2 : 1;
      ctx.stroke();

      // Flag indicator
      ctx.fillStyle = isActive ? '#ffd700' : '#888';
      ctx.font = 'bold 18px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(lang.flag, x + 30, btnY + btnH / 2);

      // Language name
      ctx.fillStyle = isActive ? '#ffd700' : '#ccc';
      ctx.font = `${isActive ? 'bold ' : ''}15px sans-serif`;
      ctx.fillText(lang.label, x + btnW / 2 + 15, btnY + btnH / 2);

      // Active checkmark
      if (isActive) {
        ctx.fillStyle = '#ffd700';
        ctx.font = 'bold 14px sans-serif';
        ctx.fillText('*', x + btnW - 18, btnY + 14);
      }
    }
  }

  private drawCredits(ctx: CanvasRenderingContext2D): void {
    ctx.textAlign = 'center';
    ctx.fillStyle = '#ffd700';
    ctx.font = 'bold 36px serif';
    ctx.fillText(t('mainMenu.backBtn').replace('← ', ''), this.width / 2, 70);

    const credits = [
      [t('mainMenu.infoGame'), t('mainMenu.gameName')],
      [t('mainMenu.infoGenre'), t('mainMenu.genre')],
      [t('mainMenu.infoDev'), t('mainMenu.developer')],
      ['Versiyon', '0.1.0 Alpha']
    ];

    let y = 180;
    for (const [label, value] of credits) {
      ctx.fillStyle = '#555';
      ctx.font = '14px sans-serif';
      ctx.fillText(label, this.width / 2, y);

      ctx.fillStyle = '#aaa';
      ctx.font = '18px sans-serif';
      ctx.fillText(value, this.width / 2, y + 24);

      y += 70;
    }

    this.drawBackButton(ctx);
  }

  private drawBackButton(ctx: CanvasRenderingContext2D): void {
    const x = 30;
    const y = this.height - 60;
    const w = 100;
    const h = 40;
    const isHovered = this.hoveredButton === 'back';

    ctx.fillStyle = isHovered ? '#2a2a35' : '#1a1a25';
    ctx.beginPath();
    ctx.roundRect(x, y, w, h, 5);
    ctx.fill();

    ctx.strokeStyle = isHovered ? '#555' : '#333';
    ctx.lineWidth = 1;
    ctx.stroke();

    ctx.fillStyle = isHovered ? '#fff' : '#888';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(t('mainMenu.backBtn'), x + w / 2, y + h / 2);
  }

  getScreen(): MenuScreen {
    return this.screen;
  }
}
